import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const RequestForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    message: ''
  });

  useEffect(() => {
    document.title = 'Submit Request - Soshniti Legal';
  }, []);

  const serviceTypes = [
    'Incorporation Services',
    'Company Formation and Registration',
    'Partnership Firm Registration',
    'LLP Registration',
    'Trust Formation',
    'Commercial Contracts',
    'Non-Disclosure Agreements (NDAs)',
    'Joint Venture Agreements',
    'Employment Contracts',
    'Taxation and Compliance',
    'Income Tax Filings',
    'GST Registration and Compliance',
    'Transfer Pricing',
    'Tax Audits and Investigations',
    'Intellectual Property Law',
    'Trademark Registration',
    'Copyright Registration',
    'Patent Filing and Prosecution',
    'Export/Import Documentation',
    'International Trade Compliance',
    'Other'
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.phone || !formData.serviceType) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Phone validation - accept international format with country code
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    const cleanPhone = formData.phone.replace(/[\s\-\(\)]/g, '');
    if (!phoneRegex.test(cleanPhone)) {
      toast.error('Please enter a valid phone number with country code (e.g., +91 9876543210)');
      return;
    }

    // Submit to backend
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    
    fetch(`${API}/legal-request`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then(response => response.json())
      .then(data => {
        toast.success('Request submitted successfully! Our team will contact you soon.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          serviceType: '',
          message: ''
        });

        setTimeout(() => {
          navigate('/');
        }, 2000);
      })
      .catch(error => {
        console.error('Error:', error);
        toast.error('Failed to submit request. Please try again.');
      });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            className="text-gray-300 hover:text-white mb-8"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>

          <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700 rounded-lg p-8 md:p-12">
            <div className="mb-8">
              <div className="h-1 w-20 bg-yellow-500 mb-4"></div>
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Submit Your Request
              </h1>
              <p className="text-gray-300 text-lg">
                Fill out the form below and our legal experts will reach out to provide tailored solutions for your business needs.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-200">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-yellow-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-200">
                    Email Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-yellow-500"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-gray-200">
                    Phone Number (with country code) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+91 98765 43210 or +971 50 123 4567"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-yellow-500"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company" className="text-gray-200">
                    Company Name (Optional)
                  </Label>
                  <Input
                    id="company"
                    type="text"
                    placeholder="Your Company"
                    value={formData.company}
                    onChange={(e) => handleChange('company', e.target.value)}
                    className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 focus:border-yellow-500"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="serviceType" className="text-gray-200">
                  Type of Service Required <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={formData.serviceType} 
                  onValueChange={(value) => handleChange('serviceType', value)}
                  required
                >
                  <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white focus:border-yellow-500">
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700 text-white">
                    {serviceTypes.map((service) => (
                      <SelectItem key={service} value={service} className="focus:bg-slate-700">
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-gray-200">
                  Additional Details or Questions
                </Label>
                <Textarea
                  id="message"
                  placeholder="Tell us more about your requirements..."
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-gray-400 min-h-[120px] focus:border-yellow-500"
                  rows={5}
                />
              </div>

              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  className="bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold px-8 py-6 text-lg transition-all duration-300 hover:scale-105"
                >
                  Submit Request
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="border-slate-600 text-gray-300 hover:bg-slate-700 hover:text-white px-8 py-6 text-lg"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RequestForm;